// TelcoTrack — map weld-layout v2 onto the D5 occupancy graph (single source of truth).

import {
  cabinetCassetteRackForType,
  cabinetCassetteRackSection,
  getRozvadecCabinetType,
  splitterRatioOutputCount,
} from './rozvadec-cabinet.constants';
import {
  canonicalRozvadecCassetteTypeId,
  getRozvadecCassetteType,
  rozvadecCassetteDisplayPosition,
  rozvadecCassetteOccupiedDisplayPositions,
  rozvadecKm5AdapterPosition,
  rozvadecKm5ConnectorPolarity,
  isRozvadecWeldTraySeat,
} from './rozvadec-cassette.constants';
import {
  occupancyCabinetCodeFromTypeId,
  occupancyConnectorId,
  resolveOccupancyCabinetTypeId,
  occupancyKm5ConnectorPrintLabel,
  occupancyMukRowPrintLabel,
  occupancyRowToken,
  occupancySpliceId,
  occupancySplitterId,
  occupancySplitterParityPosition,
  occupancySplitterPortLabel,
} from './rozvadec-occupancy-schema.ids';
import type {
  RozvadecFiberEndpoint,
  RozvadecOccupancyCassette,
  RozvadecOccupancyConnector,
  RozvadecOccupancyGenerateInput,
  RozvadecOccupancyGenerateResult,
  RozvadecOccupancyRendererId,
  RozvadecOccupancyRow,
  RozvadecOccupancySchema,
  RozvadecOccupancyServiceAssignment,
  RozvadecOccupancySplice,
  RozvadecOccupancySplitter,
  RozvadecOccupancySplitterPort,
} from './rozvadec-occupancy-schema.types';
import {
  occupancySchemaExportable,
  validateRozvadecOccupancySchema,
} from './rozvadec-occupancy-schema.validate';
import {
  isCompleteWeldIncomingRef,
  isCompleteWeldTargetRef,
  rozvadecWeldIncomingRefKey,
} from './rozvadec-weld-layout.ids';
import {
  isRozvadecBuildingDirectLink,
  isRozvadecBuildingTargetedLink,
  isRozvadecCassetteBoundDirect,
  weldLinkIncomingRefs,
  weldLinkSeat,
} from './rozvadec-weld-layout.pairs';
import type {
  RozvadecWeldIncomingRef,
  RozvadecWeldLayout,
  RozvadecWeldLink,
  RozvadecWeldTermination,
} from './rozvadec-weld-layout.types';
import { buildRayTr3OccupancySchema } from './rozvadec-occupancy-schema.raytr3';
import {
  layoutWeldConnections,
  parseOccupancyEndpointId,
} from './rozvadec-occupancy-connection';
import { deriveOccupancyServicePaths } from './rozvadec-occupancy-connection.paths';
import {
  buildRozvadecOccupancySheets,
  inferRozvadecNetworkRole,
  localityFromOccupancyInput,
} from './rozvadec-occupancy-schema.sheets';

function rendererForType(typeId: string | null | undefined): RozvadecOccupancyRendererId {
  if (typeId === 'MUK_1X24' || typeId === 'MUK_2X24') return 'D5-MUK';
  if (typeId === 'PON32') return 'D5-PON32';
  if (typeId === 'RSU_ODF') return 'D5-RAYTR3';
  if (typeId === 'OS') return 'D5-SPOJKA';
  return 'D5-HUAWEI';
}

function fiberLookup(input: RozvadecOccupancyGenerateInput) {
  const map = new Map<string, { logicalFiber: number | null; firstFeederCableId: string | null }>();
  for (const row of input.fiberNumbers ?? []) {
    map.set(`${row.cableId.trim()}#${row.physicalFiber}`, {
      logicalFiber: row.logicalFiber,
      firstFeederCableId: row.firstFeederCableId,
    });
  }
  return map;
}

function upsertFiber(
  fibers: Map<string, RozvadecFiberEndpoint>,
  ref: RozvadecWeldIncomingRef | null | undefined,
  role: RozvadecFiberEndpoint['role'],
  numbers: ReturnType<typeof fiberLookup>,
): RozvadecFiberEndpoint | null {
  if (!ref || !ref.cableId.trim()) return null;
  const key = `${ref.cableId.trim()}#${ref.physicalFiber || 0}`;
  const existing = fibers.get(key);
  const meta = numbers.get(`${ref.cableId.trim()}#${ref.physicalFiber}`);
  if (existing) {
    if (role === 'drop') existing.role = 'drop';
    if (existing.logicalFiber == null && meta?.logicalFiber != null) {
      existing.logicalFiber = meta.logicalFiber;
    }
    return existing;
  }
  const fiber: RozvadecFiberEndpoint = {
    id: `fiber:${key}`,
    cableId: ref.cableId.trim(),
    physicalFiber: ref.physicalFiber > 0 ? ref.physicalFiber : null,
    logicalFiber: meta?.logicalFiber ?? null,
    firstFeederCableId: meta?.firstFeederCableId ?? null,
    role,
  };
  fibers.set(key, fiber);
  return fiber;
}

function spliceSeatForDirect(
  cassetteId: string,
  cassetteType: string | null | undefined,
  preferred: number | null,
  used: Map<string, Set<number>>,
): number {
  const taken = used.get(cassetteId) ?? new Set<number>();
  if (preferred != null && isRozvadecWeldTraySeat(cassetteType, preferred) && !taken.has(preferred)) {
    taken.add(preferred);
    used.set(cassetteId, taken);
    return preferred;
  }
  let seat = 1;
  while (taken.has(seat)) seat += 1;
  taken.add(seat);
  used.set(cassetteId, taken);
  return seat;
}

export function buildRozvadecOccupancySchema(
  layout: RozvadecWeldLayout,
  input: RozvadecOccupancyGenerateInput = {},
): RozvadecOccupancySchema {
  const type = getRozvadecCabinetType(resolveOccupancyCabinetTypeId(input.typeId, input));
  const typeId = type?.id ?? null;
  if (typeId === 'RSU_ODF') {
    return buildRayTr3OccupancySchema(input.rsuOccupancy, input);
  }
  const cabinetCode = occupancyCabinetCodeFromTypeId(typeId);
  const rack = cabinetCassetteRackForType(typeId);
  const rendererId = rendererForType(typeId);
  const numbers = fiberLookup(input);
  const rows: RozvadecOccupancyRow[] = (rack?.sections ?? []).map((section) => ({
    id: section.id,
    label: section.nazev ?? section.id,
    positions: section.positions,
    fillFrom: section.fillFrom,
  }));

  const rowIndex = new Map(rows.map((row, index) => [row.id, index]));
  const cassetteOrdinal = new Map<string, number>();
  layout.cassettes.forEach((cassette, index) => {
    cassetteOrdinal.set(cassette.id, index + 1);
  });

  const graphCassettes: RozvadecOccupancyCassette[] = layout.cassettes.map((cassette) => {
    const def = getRozvadecCassetteType(cassette.cassetteType);
    const canonical = canonicalRozvadecCassetteTypeId(cassette.cassetteType) ?? cassette.cassetteType;
    const start = rozvadecCassetteDisplayPosition(cassette.cassetteType, cassette.position) ?? cassette.position + 1;
    const row = rows.find((item) => item.id === cassette.sectionId);
    return {
      id: cassette.id,
      cassetteType: canonical,
      printLabel: def?.printLabel ?? canonical,
      rowId: cassette.sectionId,
      rowLabel: occupancyMukRowPrintLabel(row?.label ?? cassette.sectionId, start),
      startPosition: start,
      displayPosition: start,
      occupiedPositions: rozvadecCassetteOccupiedDisplayPositions(cassette.cassetteType, cassette.position),
      adapterFamily: def?.adapterFamily ?? null,
    };
  });

  const fibers = new Map<string, RozvadecFiberEndpoint>();
  const connectors: RozvadecOccupancyConnector[] = [];
  const splices: RozvadecOccupancySplice[] = [];
  const splitters: RozvadecOccupancySplitter[] = [];
  const assignments: RozvadecOccupancyServiceAssignment[] = [];
  const spliceSeatUsed = new Map<string, Set<number>>();
  const spliceByIncoming = new Map<string, string>();

  const cassetteById = new Map(graphCassettes.map((item) => [item.id, item]));
  const terminationsById = new Map(layout.terminations.map((item) => [item.id, item]));

  for (const termination of layout.terminations) {
    if (termination.kind !== 'connector' || !termination.cassetteId || termination.slotInCassette == null) {
      continue;
    }
    const cassette = cassetteById.get(termination.cassetteId);
    const def = cassette ? getRozvadecCassetteType(cassette.cassetteType) : undefined;
    if (!cassette || !def?.adapterFamily) continue;
    const port = termination.slotInCassette;
    const adapter = rozvadecKm5AdapterPosition(def.adapterFamily, port) ?? port;
    const polarity = rozvadecKm5ConnectorPolarity(def.adapterFamily, port);
    const rowToken = occupancyRowToken(cassette.rowId, rowIndex.get(cassette.rowId) ?? 0);
    const connectorId = occupancyConnectorId({
      cabinetCode,
      rowToken,
      startPosition: cassette.startPosition,
      cassetteType: cassette.cassetteType,
      adapterFamily: def.adapterFamily,
      portOrdinal: port,
    });
    const spliceId = occupancySpliceId({
      cabinetCode,
      rowToken,
      startPosition: cassette.startPosition,
      cassetteType: cassette.cassetteType,
      cassetteOrdinal: cassetteOrdinal.get(cassette.id) ?? 1,
      seat: port,
    });
    splices.push({
      id: spliceId,
      cassetteId: cassette.id,
      seat: port,
      printLabel: `${cassette.printLabel} / svar ${port}`,
      kind: 'pigtail',
      fibers: termination.incoming ? [termination.incoming] : [],
    });
    if (termination.incoming) {
      spliceByIncoming.set(rozvadecWeldIncomingRefKey(termination.incoming), spliceId);
      upsertFiber(fibers, termination.incoming, 'feeder', numbers);
    }
    connectors.push({
      id: connectorId,
      cassetteId: cassette.id,
      adapterPosition: adapter,
      polarity,
      printLabel: occupancyKm5ConnectorPrintLabel(def.adapterFamily, port),
      incoming: termination.incoming,
      pigtailSpliceId: spliceId,
    });
  }

  const splitterTermById = new Map<string, RozvadecOccupancySplitter>();
  layout.terminations
    .filter((item) => item.kind === 'splitter')
    .forEach((termination) => {
      const cassette = termination.cassetteId ? cassetteById.get(termination.cassetteId) : undefined;
      const def = cassette ? getRozvadecCassetteType(cassette.cassetteType) : undefined;
      const ratio = termination.splitterRatio ?? def?.preinstalledSplitters?.ratio ?? '1:8';
      const outputCount = splitterRatioOutputCount(ratio) ?? 0;
      const splitterIndex =
        def?.preinstalledSplitters?.count === 2 ? (termination.slotInCassette ?? 1) : 1;
      const insideCassette =
        !!cassette && (def?.contentKinds.includes('splitter') === true || def?.id === 'KM6');
      const id = occupancySplitterId({
        cassetteId: termination.cassetteId,
        splitterIndex,
      });
      const ports = (direction: 'in' | 'out', count: number): RozvadecOccupancySplitterPort[] => {
        const list: RozvadecOccupancySplitterPort[] = [];
        for (let port = 1; port <= count; port += 1) {
          list.push({
            id: `${id}:${direction}:${port}`,
            splitterId: id,
            direction,
            splitterIndex,
            port,
            printLabel: occupancySplitterPortLabel({ splitterIndex, direction, port }),
            rowPosition:
              cassette && direction === 'out'
                ? occupancySplitterParityPosition(cassette.startPosition, port)
                : cassette
                  ? cassette.startPosition
                  : null,
            incoming: direction === 'in' ? termination.incoming : null,
            target: null,
          });
        }
        return list;
      };
      const splitter: RozvadecOccupancySplitter = {
        id,
        printLabel: `SPL-${String(splitterIndex).padStart(2, '0')}`,
        cassetteId: termination.cassetteId,
        ratio,
        splitterIndex,
        insideCassette,
        inputs: ports('in', 1),
        outputs: ports('out', outputCount),
      };
      if (termination.incoming) {
        upsertFiber(fibers, termination.incoming, 'feeder', numbers);
      }
      splitters.push(splitter);
      splitterTermById.set(termination.id, splitter);
    });

  const ensureTraySplice = (
    cassetteId: string,
    refs: RozvadecWeldIncomingRef[],
    preferredSeat?: number | null,
  ): RozvadecOccupancySplice => {
    const existing = splices.find(
      (item) =>
        item.cassetteId === cassetteId &&
        item.kind === 'tray' &&
        refs.every((ref) =>
          item.fibers.some(
            (fiber) => fiber.cableId === ref.cableId && fiber.physicalFiber === ref.physicalFiber,
          ),
        ),
    );
    if (existing) return existing;
    const cassette = cassetteById.get(cassetteId);
    const seat = spliceSeatForDirect(
      cassetteId,
      cassetteById.get(cassetteId)?.cassetteType,
      preferredSeat ?? null,
      spliceSeatUsed,
    );
    const rowToken = cassette
      ? occupancyRowToken(cassette.rowId, rowIndex.get(cassette.rowId) ?? 0)
      : 'R1';
    const splice: RozvadecOccupancySplice = {
      id: occupancySpliceId({
        cabinetCode,
        rowToken,
        startPosition: cassette?.startPosition ?? null,
        cassetteType: cassette?.cassetteType ?? 'KM6',
        cassetteOrdinal: cassetteOrdinal.get(cassetteId) ?? 1,
        seat,
      }),
      cassetteId,
      seat,
      printLabel: `${cassette?.printLabel ?? 'KM6'} / svar ${seat}`,
      kind: 'tray',
      fibers: refs,
    };
    splices.push(splice);
    for (const ref of refs) {
      spliceByIncoming.set(rozvadecWeldIncomingRefKey(ref), splice.id);
    }
    return splice;
  };

  // Direction-free fiber↔fiber welds are first-class D5 splices too. Keep both
  // physical cable/fiber references; never renumber from a logical direction.
  const fallbackWeldCassetteId =
    graphCassettes.find((cassette) => isRozvadecWeldTraySeat(cassette.cassetteType, 1))?.id
    ?? 'physical-welds';
  for (const weld of layoutWeldConnections(layout)) {
    const ends = [weld.fromEndpointId, weld.toEndpointId]
      .map(parseOccupancyEndpointId);
    if (ends[0]?.kind === 'cable-fiber' && ends[1]?.kind === 'cable-fiber') {
      const refs: RozvadecWeldIncomingRef[] = [
        { cableId: ends[0].cableId, physicalFiber: ends[0].physicalFiber },
        { cableId: ends[1].cableId, physicalFiber: ends[1].physicalFiber },
      ];
      const alreadyRendered = splices.some((splice) =>
        refs.every((ref) => splice.fibers.some((fiber) =>
          fiber.cableId === ref.cableId && fiber.physicalFiber === ref.physicalFiber,
        )),
      );
      if (alreadyRendered) continue;
      upsertFiber(fibers, refs[0], ends[0].role === 'outgoing' ? 'drop' : 'feeder', numbers);
      upsertFiber(fibers, refs[1], ends[1].role === 'outgoing' ? 'drop' : 'feeder', numbers);
      ensureTraySplice(fallbackWeldCassetteId, refs);
      continue;
    }
    const fiberEnd = ends.find((end) => end?.kind === 'cable-fiber');
    const splitterEnd = ends.find((end) => end?.kind === 'splitter-in' || end?.kind === 'splitter-out');
    if (fiberEnd?.kind !== 'cable-fiber' || !splitterEnd) continue;
    const ref: RozvadecWeldIncomingRef = {
      cableId: fiberEnd.cableId,
      physicalFiber: fiberEnd.physicalFiber,
    };
    const alreadyRendered = splices.some((splice) =>
      splice.fibers.some(
        (fiber) => fiber.cableId === ref.cableId && fiber.physicalFiber === ref.physicalFiber,
      ),
    );
    if (alreadyRendered) continue;
    upsertFiber(fibers, ref, fiberEnd.role === 'outgoing' ? 'drop' : 'feeder', numbers);
    ensureTraySplice(fallbackWeldCassetteId, [ref]);
  }

  for (const link of layout.links) {
    if (isRozvadecCassetteBoundDirect(link) && link.cassetteId) {
      const refs = weldLinkIncomingRefs(link);
      for (const ref of refs) {
        upsertFiber(fibers, ref, link.target ? 'drop' : 'reserve', numbers);
      }
      if (refs.length > 0) {
        const splice = ensureTraySplice(link.cassetteId, refs, weldLinkSeat(link));
        if (isRozvadecBuildingTargetedLink(link) && link.target) {
          const incoming = link.incoming;
          const meta = incoming
            ? numbers.get(`${incoming.cableId.trim()}#${incoming.physicalFiber}`)
            : undefined;
          assignments.push({
            id: `asg:${link.id}`,
            buildingId: link.target.buildingId,
            cableId: link.target.cableId,
            physicalFiber: link.target.physicalFiber,
            logicalFiber: meta?.logicalFiber ?? null,
            sourcePhysicalFiber: incoming?.physicalFiber ?? null,
            via: 'direct',
            connectorId: null,
            splitterPortId: null,
            spliceId: splice.id,
          });
        }
      }
      continue;
    }

    if (isRozvadecBuildingDirectLink(link) && link.incoming && link.target) {
      upsertFiber(fibers, link.incoming, 'feeder', numbers);
      upsertFiber(
        fibers,
        { cableId: link.target.cableId, physicalFiber: link.target.physicalFiber },
        'drop',
        numbers,
      );
      const cassetteId = (link.cassetteId ?? '').trim();
      const spliceId = cassetteId ? ensureTraySplice(cassetteId, [link.incoming]).id : null;
      const meta = numbers.get(`${link.incoming.cableId.trim()}#${link.incoming.physicalFiber}`);
      assignments.push({
        id: `asg:${link.id}`,
        buildingId: link.target.buildingId,
        cableId: link.target.cableId,
        physicalFiber: link.target.physicalFiber,
        logicalFiber: meta?.logicalFiber ?? null,
        sourcePhysicalFiber: link.incoming.physicalFiber,
        via: 'direct',
        connectorId: null,
        splitterPortId: null,
        spliceId,
      });
      continue;
    }

    if (link.kind === 'connector' && link.terminationId) {
      const termination = terminationsById.get(link.terminationId);
      const connector = connectors.find(
        (item) =>
          termination &&
          item.cassetteId === (termination.cassetteId ?? '') &&
          item.printLabel ===
            occupancyKm5ConnectorPrintLabel(
              cassetteById.get(termination.cassetteId ?? '')?.adapterFamily ?? 'SC',
              termination.slotInCassette ?? 0,
            ),
      );
      if (termination?.incoming) upsertFiber(fibers, termination.incoming, 'feeder', numbers);
      if (isCompleteWeldTargetRef(link.target) && link.target) {
        const incoming = termination?.incoming ?? null;
        const meta = incoming
          ? numbers.get(`${incoming.cableId.trim()}#${incoming.physicalFiber}`)
          : undefined;
        assignments.push({
          id: `asg:${link.id}`,
          buildingId: link.target.buildingId,
          cableId: link.target.cableId,
          physicalFiber: link.target.physicalFiber,
          logicalFiber: meta?.logicalFiber ?? null,
          sourcePhysicalFiber: incoming?.physicalFiber ?? null,
          via: 'connector',
          connectorId: connector?.id ?? null,
          splitterPortId: null,
          spliceId: connector?.pigtailSpliceId ?? null,
        });
      }
      continue;
    }

    if (link.kind === 'splitter' && link.terminationId) {
      const splitter = splitterTermById.get(link.terminationId);
      if (!splitter || link.splitterOutput == null) continue;
      const output = splitter.outputs.find((port) => port.port === link.splitterOutput);
      if (output) {
        output.incoming = link.incoming;
        output.target = link.target;
      }
      if (link.incoming) upsertFiber(fibers, link.incoming, link.target ? 'drop' : 'feeder', numbers);
      if (isCompleteWeldTargetRef(link.target) && link.target) {
        const incoming = link.incoming;
        const meta = incoming
          ? numbers.get(`${incoming.cableId.trim()}#${incoming.physicalFiber}`)
          : undefined;
        let spliceId: string | null = null;
        if (cabinetCode === 'PON32' && incoming) {
          const km6 = graphCassettes.find((item) => item.cassetteType === 'KM6');
          if (km6) {
            spliceId = ensureTraySplice(km6.id, [incoming]).id;
          }
        } else if (incoming) {
          spliceId = spliceByIncoming.get(rozvadecWeldIncomingRefKey(incoming)) ?? null;
        }
        assignments.push({
          id: `asg:${link.id}`,
          buildingId: link.target.buildingId,
          cableId: link.target.cableId,
          physicalFiber: link.target.physicalFiber,
          logicalFiber: meta?.logicalFiber ?? null,
          sourcePhysicalFiber: incoming?.physicalFiber ?? null,
          via: 'splitter',
          connectorId: null,
          splitterPortId: output?.id ?? null,
          spliceId,
        });
      }
    }
  }

  for (const path of deriveOccupancyServicePaths(layout)) {
    const outgoing = parseOccupancyEndpointId(path.outgoingEndpointId);
    const incoming = parseOccupancyEndpointId(path.incomingEndpointId);
    if (!path.complete || outgoing?.kind !== 'cable-fiber') continue;
    const incomingFiber = incoming?.kind === 'cable-fiber' ? incoming : null;
    const building = (input.buildings ?? []).find((item) => item.cableId === outgoing.cableId);
    if (incomingFiber) upsertFiber(fibers, incomingFiber, 'feeder', numbers);
    upsertFiber(fibers, outgoing, 'drop', numbers);
    const sc = parseOccupancyEndpointId(path.scEndpointId);
    const connector = connectors.find(
      (item) =>
        sc?.kind === 'cassette-connector'
        && item.cassetteId === sc.cassetteId
        && item.adapterPosition === sc.slotInCassette,
    );
    assignments.push({
      id: `asg:${path.id}`,
      buildingId: building?.buildingId ?? outgoing.cableId,
      cableId: outgoing.cableId,
      physicalFiber: outgoing.physicalFiber,
      logicalFiber: incomingFiber
        ? numbers.get(`${incomingFiber.cableId}#${incomingFiber.physicalFiber}`)?.logicalFiber ?? null
        : null,
      sourcePhysicalFiber: incomingFiber?.physicalFiber ?? null,
      via: 'splitter',
      connectorId: connector?.id ?? path.lcEndpointId,
      splitterPortId: path.splitterOutEndpointId,
      spliceId: connector?.pigtailSpliceId ?? null,
    });
  }

  const availableKm6 =
    input.availableKm6Count ??
    (cabinetCode === 'PON32' ? rack?.sections[0]?.positions ?? 4 : 0);
  const networkRole = inferRozvadecNetworkRole(input, typeId);
  const printName = input.cabinetPrintName ?? type?.nazev ?? 'Rozvaděč';
  const configuration =
    cabinetCode === 'MUK48' ? '2×24' : cabinetCode === 'MUK24' ? '1×24' : null;

  const schema: RozvadecOccupancySchema = {
    version: 1,
    rendererId,
    cabinetCode,
    typeId,
    sapCode: input.derivedFromSap ?? type?.identitySapCodes[0] ?? null,
    printName,
    networkRole,
    locality: localityFromOccupancyInput(input, networkRole),
    objectName: input.objectName ?? input.cabinetPrintName ?? null,
    configuration,
    rows,
    graph: {
      fibers: [...fibers.values()],
      cassettes: graphCassettes,
      connectors,
      splices,
      splitters,
      assignments,
    },
    sheets: [],
    pon32:
      cabinetCode === 'PON32'
        ? {
            pon32HardwareRevision: input.pon32HardwareRevision ?? null,
            connectorPanelConfiguration: input.connectorPanelConfiguration ?? null,
            availableKm6Count: availableKm6,
          }
        : null,
  };
  schema.sheets = buildRozvadecOccupancySheets(schema, input);
  return schema;
}

export function generateRozvadecOccupancySchema(
  layout: RozvadecWeldLayout,
  input: RozvadecOccupancyGenerateInput = {},
): RozvadecOccupancyGenerateResult {
  const schema = buildRozvadecOccupancySchema(layout, input);
  const blockers = validateRozvadecOccupancySchema(schema, layout, input);
  return {
    schema,
    blockers,
    exportable: occupancySchemaExportable(blockers),
  };
}

export function occupancySchemaHasMuk48IndependentRows(schema: RozvadecOccupancySchema): boolean {
  return (
    schema.cabinetCode === 'MUK48' &&
    schema.rows.length === 2 &&
    schema.rows.every((row) => row.positions === 24)
  );
}

/** Unused import guard for section helper — keep rack lookups local. */
export function occupancyRackSectionExists(typeId: string | null | undefined, sectionId: string): boolean {
  return cabinetCassetteRackSection(cabinetCassetteRackForType(typeId), sectionId) != null;
}

export type { RozvadecWeldLink, RozvadecWeldTermination };
